import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { LayoutChangeEvent } from 'react-native';
import { View } from 'react-native';
import {
  FadeView,
  ShadowDivider,
  useNoticeBannersStackContext,
} from '@sweetgreen/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';

import { ErrorMessage } from '../../../../components/AddressSearch/ErrorMessage';
import { LoadingResults } from '../../../../components/AddressSearch/LoadingResults';
import { NoResults } from '../../../../components/AddressSearch/NoResults';
import { BagAccessButton } from '../../../../components/Bag/subcomponents';
import { MOBILE_HEADER_HEIGHT } from '../../../../components/Header/constants';
import { useReorderState } from '../../../ReorderingScreen/hooks';
import { useNavigationActions } from '../../hooks';
import { styles } from '../../LocationScreen.styles';
import type { LocationResultsProps } from '../../LocationScreen.types';
import { LocationBottomSheet } from '../LocationBottomSheet';
import { LocationResultsV2 } from '../LocationResultsV2';
import { SearchBar } from '../SearchBar';
import { DeliveryLocationLayout } from './LocationLayout.delivery';

export const LocationLayout = (props: LocationResultsProps) => {
  const {
    locationSearchType,
    loading,
    error,
    noResults,
    children,
    userLocation,
    locations,
    locationsForSelectedSearchType,
    locationsForOppositeSearchType,
    selectedLocation,
    isShownLocationWarningModal,
    onCardPress,
  } = props;

  const { t } = useLocalizationContext();
  const navigationActions = useNavigationActions();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  const [bottomSheetTopOffset, setBottomSheetTopOffset] = useState<number>();
  const [bottomSheetExpanded, setBottomSheetExpanded] = useState(false);

  // ─────────────────────────── Telemetry ───────────────────────────────

  const currentBottomSheetState = useRef(false);

  const { track } = useTelemetry();

  const onBottomSheetExpansion = React.useCallback(
    (expanded: boolean) => {
      setBottomSheetExpanded(expanded);

      if (expanded === currentBottomSheetState.current) return;

      // eslint-disable-next-line functional/immutable-data
      currentBottomSheetState.current = expanded; // preventing repeated triggers.
      track('location.bottom_sheet', {
        state: expanded ? 'expanded' : 'collapsed',
      });
    },
    [setBottomSheetExpanded, track],
  );

  /**
   * Calculates top bar height and position to use it as a top offset for bottom sheet
   */
  const setBottomSheetTopOffsetOnLayout = useCallback(
    ({ nativeEvent }: LayoutChangeEvent) => {
      const { layout } = nativeEvent;

      setBottomSheetTopOffset(layout.height + MOBILE_HEADER_HEIGHT);
    },
    [],
  );

  // ─────────────────────────────────────────────────────────────────

  const isDeliverySearchType = locationSearchType === 'delivery';

  const {
    reordering: isReordering,
    currentStep: currentReorderStep,
    restaurantSlug: reorderRestaurantSlug,
  } = useReorderState();
  const activeReorderRestaurantSlug = isReordering
    ? reorderRestaurantSlug
    : undefined;

  const shouldRenderBagAccessBtn = currentReorderStep === 'idle';

  // NOTE: We temporarily utilize those conditions temporarily
  //       because we have two versions of location results components.

  const shouldUseLocationsResultsRedesign = useFeatureFlag(
    'XP-3156-short-locations-results-redesign',
  );
  const shouldRenderNoResultsView = Boolean(!loading && !error && noResults);
  const shouldRenderLoadingView = !shouldUseLocationsResultsRedesign && loading;
  const shouldRenderResults = !error && !noResults;
  const shouldRenderListResultsView =
    shouldRenderResults && shouldUseLocationsResultsRedesign;
  const shouldRenderBottomSheetResultsView =
    !loading && shouldRenderResults && !shouldUseLocationsResultsRedesign;

  // ─── Effects ─────────────────────────────────────────────────────

  useEffect(() => {
    const enablePermissionText = t('location.enable-location-services-mobile');

    const shouldShowEnableGeolocationMessage =
      !noResults &&
      locations.length === 0 &&
      userLocation.permissionStatus === 'denied';

    if (shouldShowEnableGeolocationMessage)
      addNoticeBanner({
        text: enablePermissionText,
        palette: 'neutral',
        autoHideTimeout: 4000,
      });
  }, [
    addNoticeBanner,
    locations.length,
    noResults,
    t,
    userLocation.permissionStatus,
  ]);

  // ─────────────────────────────────────────────────────────────────

  return (
    <View style={styles.mobileScreenContainer} testID="location-layout">
      <View
        style={styles.mobileSearchContainer}
        onLayout={setBottomSheetTopOffsetOnLayout}
      >
        <SearchBar {...props} {...navigationActions} />
      </View>

      <View style={styles.mapContainer}>
        <FadeView
          show={!bottomSheetExpanded}
          style={styles.shadowContainer}
          duration={350}
        >
          <ShadowDivider />
        </FadeView>

        {/* ---------------------------------------- */}
        {/* ---- map                                 */}
        {/* ---------------------------------------- */}

        {children}

        {isDeliverySearchType ? (
          <DeliveryLocationLayout {...props} />
        ) : (
          <>
            {shouldRenderLoadingView ? <LoadingResults /> : null}
            {error ? <ErrorMessage /> : null}
            {shouldRenderNoResultsView ? <NoResults /> : null}

            {shouldRenderBottomSheetResultsView ? (
              <LocationBottomSheet
                topOffset={bottomSheetTopOffset}
                setBottomSheetExpanded={onBottomSheetExpansion}
                {...props}
              />
            ) : null}

            {shouldRenderListResultsView ? (
              <LocationResultsV2
                locations={locationsForSelectedSearchType}
                otherLocations={locationsForOppositeSearchType}
                activePinLocationId={selectedLocation}
                locationSearchType={locationSearchType}
                isLoading={loading}
                activeReorderRestaurantSlug={activeReorderRestaurantSlug}
                shouldHideDetailsModal={isShownLocationWarningModal}
                onCardPress={onCardPress}
              />
            ) : null}
          </>
        )}
      </View>

      {shouldRenderBagAccessBtn ? <BagAccessButton /> : null}
    </View>
  );
};
